import type { UserListQuery } from '@chukua/common';
import { Tag, type TableColumnsType } from 'antd';
import type { ListQueryControls } from '../../lib/use-list-query';
import type { RoleWithPermissions } from '../roles/roles.api';
import { UserRolesCell } from './UserRolesCell';
import { UserRowActions } from './UserRowActions';
import type { UserListItem } from './users.api';

interface BuildUserColumnsArgs {
  roleOptions: RoleWithPermissions[];
  /** The signed-in user, whose own row is marked "You" and can't be deactivated or re-roled. */
  currentUserId: string | undefined;
  list: Pick<ListQueryControls<UserListQuery>, 'sortOrderFor' | 'filteredValueFor'>;
  onSetRoles: (user: UserListItem, roles: string[]) => Promise<void>;
  onResetPassword: (user: UserListItem) => void;
  onDeactivate: (user: UserListItem) => Promise<void>;
  onReactivate: (user: UserListItem) => Promise<void>;
}

const STATUS_FILTERS = [
  { text: 'Active', value: 'active' },
  { text: 'Inactive', value: 'inactive' },
];

function actionsColumn({
  currentUserId,
  onResetPassword,
  onDeactivate,
  onReactivate,
}: Pick<BuildUserColumnsArgs, 'currentUserId' | 'onResetPassword' | 'onDeactivate' | 'onReactivate'>): TableColumnsType<UserListItem>[number] {
  return {
    title: '',
    key: 'actions',
    render: (_, user) => (
      <UserRowActions user={user} isYou={user.id === currentUserId} onResetPassword={onResetPassword} onDeactivate={onDeactivate} onReactivate={onReactivate} />
    ),
  };
}

/** Sorting and filtering run on the server: each column's `key` is its `sort` field or filter param in `userListQuerySchema`. */
export function buildUserColumns({
  roleOptions,
  currentUserId,
  list,
  onSetRoles,
  onResetPassword,
  onDeactivate,
  onReactivate,
}: BuildUserColumnsArgs): TableColumnsType<UserListItem> {
  return [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: list.sortOrderFor('name'),
      render: (name: string, user) => (
        <span className="inline-flex items-center gap-2">
          {name}
          {user.id === currentUserId && <Tag color="processing">You</Tag>}
        </span>
      ),
    },
    { title: 'Email', dataIndex: 'email', key: 'email', sorter: true, sortOrder: list.sortOrderFor('email') },
    { title: 'Phone', dataIndex: 'phone', key: 'phone', render: (phone: string | null) => phone ?? '—' },
    {
      title: 'Roles',
      key: 'role',
      filters: roleOptions.map((role) => ({ text: role.name, value: role.key })),
      filteredValue: list.filteredValueFor('role'),
      render: (_, user) => (
        <UserRolesCell roles={user.roles} roleOptions={roleOptions} canEdit={user.id !== currentUserId} onSave={(roles) => onSetRoles(user, roles)} />
      ),
    },
    {
      title: 'Status',
      key: 'status',
      filters: STATUS_FILTERS,
      filteredValue: list.filteredValueFor('status'),
      render: (_, user) => <Tag color={user.isActive ? 'success' : 'default'}>{user.isActive ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'Last login',
      key: 'lastLoginAt',
      sorter: true,
      sortOrder: list.sortOrderFor('lastLoginAt'),
      render: (_, user) => (user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'),
    },
    actionsColumn({ currentUserId, onResetPassword, onDeactivate, onReactivate }),
  ];
}
